import { useEffect, useRef, type RefObject } from 'react';
import { shouldRestoreFocus } from '@/features/common/focusAfterBusy';

/**
 * Puts the focus back on `target` when `busy` ends with the window still open and the focus
 * outside it — the submit button a modal window disables while its request is out (see
 * shouldRestoreFocus). Shared by the role and the event windows.
 */
export function useFocusAfterBusy(
  container: RefObject<HTMLElement>,
  target: RefObject<HTMLElement>,
  busy: boolean,
  open: boolean,
) {
  const wasBusy = useRef(busy);

  useEffect(() => {
    const root = container.current;
    const restore = shouldRestoreFocus({
      wasBusy: wasBusy.current,
      busy,
      open,
      focusInside: !!root && root.contains(document.activeElement),
    });
    wasBusy.current = busy;
    if (restore) target.current?.focus();
    // Only when `busy` changes: the focus is the window's own business the rest of the time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);
}
